import { useAccount, useChainId, useReadContract } from 'wagmi'
import { keccak256, toBytes, zeroHash } from 'viem'
import { referralManagerAbi } from '@/abi/referralManagerAbi'
import { operatorAbi } from '@/abi/operatorAbi'
import { getContractAddress } from '@/config/contracts'

const OPERATOR_ROLE = keccak256(toBytes('OPERATOR_ROLE'))
const TREASURY_ROLE = keccak256(toBytes('TREASURY_ROLE'))

// On-chain preflight for the referrals page. The console's own RoleGate covers
// console-session roles only; the ReferralManager transactions are gated by its separate
// on-chain AccessControl roles, and referral routing additionally requires the operator to point
// at this ReferralManager (script 07 can complete without wiring it on hosted testnets).
// Reading all of it up front lets the UI explain problems instead of surfacing bare
// AccessControlUnauthorizedAccount reverts from the wallet.
export function useReferralAccess() {
    const chainId = useChainId()
    const { address: account } = useAccount()
    const manager = getContractAddress(chainId, 'referralManager')
    const operator = getContractAddress(chainId, 'operator')
    const roleEnabled = Boolean(manager && account)

    const isOperator = useReadContract({
        address: manager,
        abi: referralManagerAbi,
        functionName: 'hasRole',
        args: [OPERATOR_ROLE, account ?? '0x0000000000000000000000000000000000000000'],
        chainId,
        query: { enabled: roleEnabled },
    })
    const isTreasury = useReadContract({
        address: manager,
        abi: referralManagerAbi,
        functionName: 'hasRole',
        args: [TREASURY_ROLE, account ?? '0x0000000000000000000000000000000000000000'],
        chainId,
        query: { enabled: roleEnabled },
    })
    const isRmAdmin = useReadContract({
        address: manager,
        abi: referralManagerAbi,
        functionName: 'hasRole',
        args: [zeroHash, account ?? '0x0000000000000000000000000000000000000000'],
        chainId,
        query: { enabled: roleEnabled },
    })
    const isLgoAdmin = useReadContract({
        address: operator,
        abi: operatorAbi,
        functionName: 'hasRole',
        args: [zeroHash, account ?? '0x0000000000000000000000000000000000000000'],
        chainId,
        query: { enabled: Boolean(operator && account) },
    })
    const lgoManager = useReadContract({
        address: operator,
        abi: operatorAbi,
        functionName: 'referralManager',
        chainId,
        query: { enabled: Boolean(operator && manager) },
    })

    // Unknown wiring (no operator configured, read still loading) reports wired so the warning
    // only ever asserts a confirmed mismatch.
    const wired =
        lgoManager.data === undefined
            ? true
            : lgoManager.data.toLowerCase() === (manager ?? '').toLowerCase()

    return {
        account,
        isOperator: isOperator.data === true,
        isTreasury: isTreasury.data === true,
        isRmAdmin: isRmAdmin.data === true,
        isLgoAdmin: isLgoAdmin.data === true,
        wired,
        isLoading:
            isOperator.isLoading ||
            isTreasury.isLoading ||
            isRmAdmin.isLoading ||
            isLgoAdmin.isLoading ||
            lgoManager.isLoading,
    }
}
